/**
 * Admin Notifications Page
 *
 * Displays notification monitoring with two tabs:
 * - Overview: Stats cards, notification volume by type, alert config summary
 * - Recent Activity: Filterable list of recent notifications with pagination
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API Helpers ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface NotificationStats {
  total: number;
  byType: Record<string, number>;
  last24h: {
    total: number;
    byType: Record<string, number>;
  };
  email: {
    sent: number;
    pending: number;
  };
  readRate: number;
  alertConfigs: {
    total: number;
    byType: Record<string, number>;
    topCollections: Array<{
      collection_id: string;
      collection_name: string;
      collection_slug: string;
      alert_count: number;
    }>;
  };
}

interface EmailHealth {
  hourlyBreakdown: Array<{
    hour: string;
    created_count: number;
    sent_count: number;
  }>;
  stuckEmails: {
    count: number;
    items: Array<{
      id: string;
      user_id: string;
      type: string;
      created_at: string;
      username: string | null;
      xrpl_address: string;
    }>;
  };
  emailEnabledUsers: number;
}

interface RecentNotification {
  id: string;
  user_id: string;
  type: string;
  data: any;
  read_at: string | null;
  email_sent_at: string | null;
  created_at: string;
  username: string | null;
  xrpl_address: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ==================== Constants ====================

const NOTIFICATION_TYPES = [
  'NFT_RECEIVED_OFFER',
  'NFT_BOUGHT',
  'NFT_SOLD',
  'COLLECTION_NEW_LISTING',
  'COLLECTION_NEW_FLOOR',
  'COLLECTION_SALE',
] as const;

const TYPE_BADGE_COLORS: Record<string, string> = {
  NFT_RECEIVED_OFFER: 'bg-blue-100 text-blue-800',
  NFT_BOUGHT: 'bg-green-100 text-green-800',
  NFT_SOLD: 'bg-purple-100 text-purple-800',
  COLLECTION_NEW_LISTING: 'bg-orange-100 text-orange-800',
  COLLECTION_NEW_FLOOR: 'bg-yellow-100 text-yellow-800',
  COLLECTION_SALE: 'bg-teal-100 text-teal-800',
};

const TYPE_LABELS: Record<string, string> = {
  NFT_RECEIVED_OFFER: 'Received Offer',
  NFT_BOUGHT: 'Bought',
  NFT_SOLD: 'Sold',
  COLLECTION_NEW_LISTING: 'New Listing',
  COLLECTION_NEW_FLOOR: 'New Floor',
  COLLECTION_SALE: 'Sale',
};

type EmailFilterTab = 'all' | 'sent' | 'pending';

// ==================== Helpers ====================

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatHour(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
}

function truncateAddress(address: string): string {
  if (!address) return '-';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// ==================== Sub-components ====================

function StatCard({ label, value, color = 'gray', alert = false }: {
  label: string;
  value: string | number;
  color?: string;
  alert?: boolean;
}) {
  const colorMap: Record<string, string> = {
    gray: 'border-gray-200 bg-white',
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    purple: 'border-purple-200 bg-purple-50',
    red: 'border-red-200 bg-red-50',
    orange: 'border-orange-200 bg-orange-50',
  };
  const textColorMap: Record<string, string> = {
    gray: 'text-gray-500',
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    red: 'text-red-600',
    orange: 'text-orange-600',
  };
  const valueColorMap: Record<string, string> = {
    gray: 'text-gray-900',
    blue: 'text-blue-700',
    green: 'text-green-700',
    purple: 'text-purple-700',
    red: 'text-red-700',
    orange: 'text-orange-700',
  };

  return (
    <div className={`rounded-lg border p-4 shadow-sm ${alert ? 'border-red-300 bg-red-50' : colorMap[color] || colorMap.gray}`}>
      <div className={`text-sm font-medium ${alert ? 'text-red-600' : textColorMap[color] || textColorMap.gray}`}>
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${alert ? 'text-red-700' : valueColorMap[color] || valueColorMap.gray}`}>
        {value}
      </div>
    </div>
  );
}

function NotificationTypeBadge({ type }: { type: string }) {
  const colors = TYPE_BADGE_COLORS[type] || 'bg-gray-100 text-gray-800';
  const label = TYPE_LABELS[type] || type;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors}`}>
      {label}
    </span>
  );
}

// ==================== Main Component ====================

export function AdminNotificationsPage() {
  const [activeMainTab, setActiveMainTab] = useState<'overview' | 'recent'>('overview');
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [emailHealth, setEmailHealth] = useState<EmailHealth | null>(null);
  const [notifications, setNotifications] = useState<RecentNotification[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recent activity filters
  const [emailFilter, setEmailFilter] = useState<EmailFilterTab>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  // ==================== Data Loading ====================

  const loadOverviewData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, healthRes] = await Promise.all([
        fetchWithAuth('/admin/notifications/stats'),
        fetchWithAuth('/admin/notifications/email-health'),
      ]);
      setStats(statsRes.data);
      setEmailHealth(healthRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load notification stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (emailFilter !== 'all') params.set('email_status', emailFilter);

      const res = await fetchWithAuth(`/admin/notifications/recent?${params.toString()}`);
      setNotifications(res.data.notifications);
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load recent notifications');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, emailFilter]);

  useEffect(() => {
    if (activeMainTab === 'overview') {
      loadOverviewData();
    } else {
      loadRecentData();
    }
  }, [activeMainTab, loadOverviewData, loadRecentData]);

  const handleRefresh = () => {
    if (activeMainTab === 'overview') {
      loadOverviewData();
    } else {
      loadRecentData();
    }
  };

  const handleEmailFilterChange = (filter: EmailFilterTab) => {
    setEmailFilter(filter);
    setPage(1);
  };

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
    setPage(1);
  };

  // ==================== Render ====================

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
            <p className="mt-2 text-sm text-gray-600">
              Monitor notification delivery and alert configurations
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Main Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveMainTab('overview')}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeMainTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveMainTab('recent')}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeMainTab === 'recent'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              Recent Activity
            </button>
          </nav>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRefresh} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && !stats && !notifications.length && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading notifications data...</p>
            </div>
          </div>
        )}

        {/* ==================== Overview Tab ==================== */}
        {activeMainTab === 'overview' && stats && emailHealth && (
          <div className="space-y-6">
            {/* Stats Cards Row 1 */}
            <div className="grid gap-4 sm:grid-cols-4">
              <StatCard label="Total Notifications" value={stats.total.toLocaleString()} color="blue" />
              <StatCard label="24h Created" value={stats.last24h.total.toLocaleString()} color="green" />
              <StatCard label="Read Rate" value={`${stats.readRate}%`} color="purple" />
              <StatCard label="Email-Enabled Users" value={emailHealth.emailEnabledUsers.toLocaleString()} color="gray" />
            </div>

            {/* Stats Cards Row 2 */}
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="Emails Sent (24h)" value={stats.email.sent.toLocaleString()} color="green" />
              <StatCard label="Pending Emails" value={stats.email.pending.toLocaleString()} color="orange" />
              <StatCard
                label="Stuck Emails (>30min)"
                value={emailHealth.stuckEmails.count}
                color={emailHealth.stuckEmails.count > 0 ? 'red' : 'gray'}
                alert={emailHealth.stuckEmails.count > 0}
              />
            </div>

            {/* Notification Volume by Type */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Notification Volume by Type</h2>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">24h</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Volume</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {NOTIFICATION_TYPES.map((type) => {
                    const total = stats.byType[type] || 0;
                    const last24h = stats.last24h.byType[type] || 0;
                    const maxTotal = Math.max(...Object.values(stats.byType), 1);
                    const barWidth = Math.max((total / maxTotal) * 100, 1);
                    return (
                      <tr key={type}>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <NotificationTypeBadge type={type} />
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {total.toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {last24h.toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="w-full bg-gray-100 rounded-full h-2.5">
                            <div
                              className="bg-blue-500 h-2.5 rounded-full"
                              style={{ width: `${barWidth}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Alert Config Summary */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Top Collections */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">
                  Top Collections by Alerts
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({stats.alertConfigs.total} total configs)
                  </span>
                </h2>
                {stats.alertConfigs.topCollections.length === 0 ? (
                  <p className="text-sm text-gray-500">No alert configurations found.</p>
                ) : (
                  <div className="space-y-3">
                    {stats.alertConfigs.topCollections.map((col, i) => (
                      <div key={col.collection_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-400 w-5">{i + 1}.</span>
                          <span className="text-sm font-medium text-gray-900">{col.collection_name}</span>
                          <span className="text-xs text-gray-400">/{col.collection_slug}</span>
                        </div>
                        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          {col.alert_count} alerts
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Alert Configs by Type */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Alert Configs by Type</h2>
                {Object.keys(stats.alertConfigs.byType).length === 0 ? (
                  <p className="text-sm text-gray-500">No alert configurations found.</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(stats.alertConfigs.byType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <NotificationTypeBadge type={type} />
                        <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Hourly Email Breakdown */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Email Delivery (24h Hourly)</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Hour</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sent</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Delivery</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {emailHealth.hourlyBreakdown.map((row) => {
                      const maxCreated = Math.max(...emailHealth.hourlyBreakdown.map(r => r.created_count), 1);
                      const createdWidth = Math.max((row.created_count / maxCreated) * 100, 0);
                      const sentWidth = Math.max((row.sent_count / maxCreated) * 100, 0);
                      return (
                        <tr key={row.hour}>
                          <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                            {formatHour(row.hour)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {row.created_count}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {row.sent_count}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <div className="flex gap-1 items-center">
                              <div className="w-24 bg-gray-100 rounded-full h-2">
                                <div className="bg-blue-400 h-2 rounded-full" style={{ width: `${createdWidth}%` }}></div>
                              </div>
                              <div className="w-24 bg-gray-100 rounded-full h-2">
                                <div className="bg-green-400 h-2 rounded-full" style={{ width: `${sentWidth}%` }}></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <div className="h-2 w-4 rounded-full bg-blue-400"></div> Created
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2 w-4 rounded-full bg-green-400"></div> Sent
                </div>
              </div>
            </div>

            {/* Stuck Emails Detail */}
            {emailHealth.stuckEmails.count > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-red-800">
                  Stuck Emails ({emailHealth.stuckEmails.count})
                </h2>
                <p className="mb-4 text-sm text-red-600">
                  These notifications are older than 30 minutes and have not been emailed. The 5-minute digest cron may be failing.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-red-200">
                    <thead className="bg-red-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-red-700">User</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-red-700">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-red-700">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-200">
                      {emailHealth.stuckEmails.items.slice(0, 20).map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <div className="text-sm text-red-900">
                              {item.username || truncateAddress(item.xrpl_address)}
                            </div>
                            {item.username && (
                              <div className="text-xs text-red-500 font-mono">
                                {truncateAddress(item.xrpl_address)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <NotificationTypeBadge type={item.type} />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-red-700">
                            {formatDate(item.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== Recent Activity Tab ==================== */}
        {activeMainTab === 'recent' && (
          <div className="space-y-4">
            {/* Filter Row */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
              {/* Email Status Tabs */}
              <div className="flex gap-2">
                {([
                  { key: 'all' as EmailFilterTab, label: 'All' },
                  { key: 'sent' as EmailFilterTab, label: 'Sent' },
                  { key: 'pending' as EmailFilterTab, label: 'Pending Email' },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleEmailFilterChange(tab.key)}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      emailFilter === tab.key
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => handleTypeFilterChange(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {NOTIFICATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {TYPE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </div>

            {/* Empty State */}
            {!loading && notifications.length === 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
                <p className="text-gray-500">No notifications found matching the current filters.</p>
              </div>
            )}

            {/* Notifications Table */}
            {notifications.length > 0 && (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email Sent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Read</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {notifications.map((n) => (
                        <tr key={n.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {n.username || truncateAddress(n.xrpl_address)}
                            </div>
                            {n.username && (
                              <div className="text-xs text-gray-500 font-mono">
                                {truncateAddress(n.xrpl_address)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <NotificationTypeBadge type={n.type} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(n.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {n.email_sent_at ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {formatDate(n.email_sent_at)}
                              </span>
                            ) : (
                              <span className="text-yellow-600">Pending</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {n.read_at ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Read
                              </span>
                            ) : (
                              <span className="text-gray-400">Unread</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} notifications
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(1)}
                        disabled={pagination.page <= 1}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={pagination.page <= 1}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                        disabled={pagination.page >= pagination.totalPages}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setPage(pagination.totalPages)}
                        disabled={pagination.page >= pagination.totalPages}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
